import { useCallback, useEffect, useState } from 'react';
import { billingErrorMessage, isBillingUnavailable, isPermissionDenied } from './billingErrors';

export type Resource<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  /** The server answered 403: this user may not see it (`billing.read` missing). */
  | { status: 'denied' }
  /** The server answered 503 `BILLING_UNAVAILABLE`: no billing provider in this environment. */
  | { status: 'unavailable' }
  | { status: 'error'; message: string };

/**
 * Loads one piece of billing data with honest loading / denied / unavailable / error states and a retry. Each billing
 * section owns its own resource, so one failing section never blanks the rest of the page, and a failed lookup is
 * never shown as "empty" (an empty list is only ever what the server actually returned). The `load` function must be
 * stable (wrap it in `useCallback`); a new identity starts a new load.
 */
export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  errorFallback: string,
  options: { keepPrevious?: boolean } = {},
): { state: Resource<T>; reload: () => void } {
  const keepPrevious = options.keepPrevious ?? true;
  const [state, setState] = useState<Resource<T>>({ status: 'loading' });
  const [token, setToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    // Keep showing what was already loaded while a refresh is in flight (so a success message or a half-typed form is
    // never wiped by a background re-read); a first load, a retry after an error, or `keepPrevious: false` shows loading.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState((previous) => (keepPrevious && previous.status === 'ready' ? previous : { status: 'loading' }));
    // `async` so that even a loader that throws before returning a promise ends in the error state, never in a crash.
    (async () => load(controller.signal))()
      .then((data) => {
        if (!controller.signal.aborted) setState({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (isPermissionDenied(err)) setState({ status: 'denied' });
        else if (isBillingUnavailable(err)) setState({ status: 'unavailable' });
        else setState({ status: 'error', message: billingErrorMessage(err, errorFallback) });
      });
    return () => controller.abort();
  }, [load, errorFallback, keepPrevious, token]);

  const reload = useCallback(() => setToken((n) => n + 1), []);
  return { state, reload };
}
